import { HttpClient } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {BookingProviderDetails} from '../types/booking-provider-details';
import {BookingRequest} from '../types/booking-request';
import {BookingResponse} from '../types/booking-response';
import {BookingMetadata} from '../types/booking-metadata';
import {CalendarResponse} from '../types/calendar-response';
import {StaffMembersSelectorEnum} from '../enums/booking-enums';
import {BookingV2ApiHelper} from './booking-v2-api.helper';
import {ToolsService} from '../../../services/tools.service';
import {AppConfigService} from '../../../services/app-config.service';
import {SessionService} from '../../../services/session.service';

enum BookingSendQueryParamsEnum {
  CARESERVICE_ID = 'cs',
  THERAPIST_ID = 'th',
}

enum BookingEndpointEnum {
  DEFAULT = 'book-new',
  BANKID = 'book-bankid',
}

@Injectable({
  providedIn: 'any',
})
export class BookingV2ApiService {
  constructor(
    private http: HttpClient,
    private apiHelper: BookingV2ApiHelper,
    private tools: ToolsService,
    private appConfigService: AppConfigService,
    private session: SessionService,
  ) {}

  public getBookingProviderDetails(id: number, careserviceId: number): Observable<BookingProviderDetails> {
    return this.http
      .get<BookingProviderDetails>(
        `${this.appConfigService.appConfig.apiUrl}bookingsystem/${id}/${careserviceId || ''}?cb=${Math.random()}`,
      )
      .pipe(catchError((error) => this.apiHelper.handleError(error, id)));
  }

  public sendBooking(
    careProviderUnitId: number,
    treatmentId: string,
    terapeutId: string,
    careServiceId: number,
    twoFactorAuthenticationRequired: boolean,
    payload: BookingRequest,
  ): Observable<BookingResponse> {
    const terapeutIdParam: string = terapeutId === StaffMembersSelectorEnum.OPTION_ANY_ID ? `` : `${terapeutId}`;

    // mock
    // use it if you want to test booking confirmation window without send booking to the backend
    // const mock: any = {
    //   careProviderUnitName: 'Tandläkare Peter Malmberg',
    //   date: '2021-10-30',
    //   serviceName: 'Undersökning i tandvården',
    //   servicePrice: '10,00',
    //   terapeutName: 'Agda Leg A. G. Hultenberger Mattinger',
    //   time: '08:00',
    // };
    // const results: BehaviorSubject<BookingResponse> = new BehaviorSubject(mock);
    // return results.asObservable();
    // mock end

    const endpoint: string = twoFactorAuthenticationRequired ? BookingEndpointEnum.BANKID : BookingEndpointEnum.DEFAULT;
    let url: string = `${this.appConfigService.appConfig.apiUrl}bookingsystem/${careProviderUnitId}/${treatmentId}/${endpoint}`;
    const params: string[] = [];
    if (!this.tools.isNil(careServiceId)) {
      params.push(`${BookingSendQueryParamsEnum.CARESERVICE_ID}=${careServiceId}`);
    }
    if (!this.tools.isNil(terapeutIdParam) && terapeutIdParam !== '') {
      params.push(`${BookingSendQueryParamsEnum.THERAPIST_ID}=${terapeutIdParam}`);
    }
    if (params.length > 0) {
      url = `${url}?${params.join('&')}`;
    }

    return this.http.post<BookingResponse>(url, payload, {withCredentials: true});
  }

  public getBookingMetadata(id: number): Observable<Array<BookingMetadata>> {
    return this.http
      .get<Array<BookingMetadata>>(
        `${this.appConfigService.appConfig.apiUrl}bookingsystem/${id}/registration/metadata?cb=${Math.random()}`,
      )
      .pipe(catchError((error) => this.apiHelper.handleError(error, id)));
  }

  public getBookingCalendar(
    careProviderUnitId: number,
    serviceId: string,
    staffId: string,
    firstDay: string,
    lastDay: string,
  ): Observable<CalendarResponse> {
    const staffIdParam =
      staffId === StaffMembersSelectorEnum.OPTION_ANY_ID || staffId === StaffMembersSelectorEnum.OPTION_NO_SELECTION
        ? ''
        : `${staffId}`;
    return this.http
      .get<CalendarResponse>(
        `${
          this.appConfigService.appConfig.apiUrl
        }bookingsystem/${careProviderUnitId}/${serviceId}/calendar/${firstDay}/${lastDay}/${staffIdParam}?cb=${Math.random()}`,
      )
      .pipe(catchError((error) => this.apiHelper.handleError(error, careProviderUnitId)));
  }
}
